// BlogDetalle.tsx — Co Marca Group — Detalle de publicación (navega por slug)
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPublicaciones, fetchPublicacionPorSlug, Publicacion } from '../../services/landing/blogService';
import { SAMPLE_BLOG_POSTS } from '../../data/sampleBlogPosts';
import DOMPurify from 'dompurify';
import { useTheme } from '../../context/ThemeContext';
import BorderGlow from '../../components/common/BorderGlow';

const colors = {
    primary: '#61257B',
    primaryDark: '#451759',
    secondary: '#C1348B',
    secondaryLight: '#FFD97C',
    accent: '#FFD97C',
    accentDark: '#EA8406',
};

const paletteColors = [colors.primary, colors.secondary, '#EA8406', '#8f1f64', '#5b21b6', colors.primaryDark];
const paletteGrads = [
    `linear-gradient(145deg,${colors.primaryDark},${colors.primary})`,
    `linear-gradient(145deg,#61257B,${colors.secondary})`,
    `linear-gradient(145deg,#451759,${colors.secondary})`,
    `linear-gradient(145deg,#7a4800,${colors.accentDark})`,
    'linear-gradient(145deg,#3b0764,#5b21b6)',
    `linear-gradient(145deg,${colors.primaryDark},#6b0032)`,
];
const emojis = ['🚀', '🤖', '💰', '🤝', '🌐', '📊', '📈', '🧠', '✍️', '💡', '🎯', '📌'];

// Funciones auxiliares (reciben solo índice)
const postColor = (i: number) => paletteColors[i % paletteColors.length];
const postGrad = (i: number) => paletteGrads[i % paletteGrads.length];
const postEmoji = (i: number) => emojis[i % emojis.length];
const initials = (n: string) => n.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const fmtDate = (d: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('es-BO', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d || ''; }
};

const BlogDetalle: React.FC = () => {
    const { isDark } = useTheme();
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [post, setPost] = useState<Publicacion | null>(null);
    const [otros, setOtros] = useState<Publicacion[]>([]);
    const [loading, setLoading] = useState(true);
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        setImgError(false);
        Promise.all([
            fetchPublicacionPorSlug(slug).catch(() => null),
            fetchPublicaciones({ estado: 'publicado' }).catch(() => []),
        ])
            .then(([pub, pubs]) => {
                const sampleFound = SAMPLE_BLOG_POSTS.find(p => p.slug === slug);
                const resolvedPost = pub || sampleFound || null;
                const availablePubs = (pubs && pubs.length > 0) ? pubs : SAMPLE_BLOG_POSTS;
                setPost(resolvedPost);
                setOtros(availablePubs.filter(p => p.slug !== slug).slice(0, 3));
            })
            .catch(() => {
                const sampleFound = SAMPLE_BLOG_POSTS.find(p => p.slug === slug);
                setPost(sampleFound || null);
                setOtros(SAMPLE_BLOG_POSTS.filter(p => p.slug !== slug).slice(0, 3));
            })
            .finally(() => setLoading(false));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [slug]);

    const slugIdx = slug ? slug.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % paletteColors.length : 0;
    const color = postColor(slugIdx);
    const grad = postGrad(slugIdx);
    const emoji = postEmoji(slugIdx);
    const imgSrc = post ? (post.url_imagen || post.imagen || null) : null;

    if (loading) return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');*{box-sizing:border-box;}@keyframes spin{to{transform:rotate(360deg);}}`}</style>
            <div style={{ fontFamily: "'DM Sans',sans-serif", minHeight: '100vh', background: isDark ? '#0b0614' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ width: '48px', height: '48px', border: `3px solid ${colors.primary}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 14px' }} />
                    <p style={{ color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '600' }}>Cargando artículo...</p>
                </div>
            </div>
        </>
    );

    if (!post) return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');*{box-sizing:border-box;}`}</style>
            <div style={{ fontFamily: "'DM Sans',sans-serif", minHeight: '100vh', background: isDark ? '#0b0614' : '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '40px' }}>
                <div style={{ fontSize: '4rem' }}>📭</div>
                <p style={{ fontSize: '1.2rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a' }}>Artículo no encontrado</p>
                <button onClick={() => navigate('/blog')} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: `linear-gradient(135deg,${colors.primary},${colors.primaryDark})`, color: '#fff', border: 'none', borderRadius: '11px', padding: '10px 22px', fontWeight: '800', fontSize: '.88rem', cursor: 'pointer', boxShadow: `0 4px 14px ${colors.primary}28` }}>
                    ← Volver al Blog
                </button>
            </div>
        </>
    );

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
                *{box-sizing:border-box;margin:0;padding:0;}
                @keyframes blobFloat{0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%;}50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%;}}
                @keyframes fadeUp{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);}}
                .det-blob{animation:blobFloat 9s ease-in-out infinite;}
                .det-in{animation:fadeUp .6s cubic-bezier(.16,1,.3,1) both;}
                .det-in-1{animation:fadeUp .6s .08s cubic-bezier(.16,1,.3,1) both;}
                .det-in-2{animation:fadeUp .6s .16s cubic-bezier(.16,1,.3,1) both;}
                .det-prose{font-size:1.05rem;line-height:1.9;color:${isDark ? '#cbd5e1' : '#374151'};}
                .det-prose h1,.det-prose h2,.det-prose h3{font-weight:900;color:${isDark ? '#ffffff' : '#0f172a'};letter-spacing:-.02em;margin:2em 0 .6em;}
                .det-prose h1{font-size:1.8rem;}.det-prose h2{font-size:1.4rem;}.det-prose h3{font-size:1.15rem;}
                .det-prose p{margin-bottom:1.25em;}
                .det-prose ul,.det-prose ol{padding-left:1.5em;margin-bottom:1.25em;}
                .det-prose li{margin-bottom:.5em;}
                .det-prose strong{font-weight:800;color:${isDark ? '#ffffff' : '#0f172a'};}
                .det-prose em{font-style:italic;}
                .det-prose a{color:${isDark ? '#FFD97C' : colors.secondary};text-decoration:underline;}
                .det-prose blockquote{border-left:4px solid ${colors.primary};margin:1.5em 0;padding:.5em 1.2em;background:${isDark ? 'rgba(193,52,139,0.12)' : `${colors.primary}06`};border-radius:0 12px 12px 0;font-style:italic;color:${isDark ? '#cbd5e1' : '#64748b'};}
                .det-prose img{max-width:100%;border-radius:16px;margin:1.5em 0;}
                .det-prose pre{background:#0a0512;color:#e2e8f0;border-radius:12px;padding:20px;overflow-x:auto;font-size:.88rem;margin:1.5em 0;border:1px solid rgba(193,52,139,0.25);}
                .det-prose code{background:${isDark ? '#1a0e2b' : '#f1f5f9'};color:${isDark ? '#FFD97C' : colors.primary};padding:2px 6px;border-radius:5px;font-size:.88em;}
                .det-prose pre code{background:transparent;color:inherit;padding:0;}
                .det-btn-back{display:inline-flex;align-items:center;gap:7px;background:${isDark ? '#150d24' : '#fff'};border:1.5px solid ${isDark ? 'rgba(193,52,139,0.3)' : '#e2e8f0'};color:${isDark ? '#e2e8f0' : '#374151'};border-radius:11px;padding:9px 18px;font-size:.85rem;font-weight:700;cursor:pointer;transition:all .2s;font-family:'DM Sans',sans-serif;}
                .det-btn-back:hover{background:${isDark ? 'rgba(193,52,139,0.2)' : '#f8fafc'};border-color:${colors.secondary};color:${isDark ? '#FFD97C' : colors.secondary};}
            `}</style>

            <div style={{ fontFamily: "'DM Sans',sans-serif", background: isDark ? '#0b0614' : '#f8fafc', minHeight: '100vh', transition: 'background 0.3s ease' }}>
                {/* HERO */}
                <div style={{
                    background: isDark
                        ? 'linear-gradient(135deg, #0b0614 0%, #150a26 55%, #200d36 100%)'
                        : '#fff',
                    borderBottom: isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9',
                    position: 'relative', overflow: 'hidden'
                }}>
                    <div className="det-blob" style={{ position: 'absolute', top: '-60px', right: '-60px', width: '360px', height: '360px', background: isDark ? 'linear-gradient(135deg, rgba(193,52,139,0.18), rgba(97,37,123,0.12))' : `linear-gradient(135deg,${color}14,${colors.secondary}0a)`, pointerEvents: 'none', zIndex: 0 }} />
                    <div style={{ position: 'absolute', bottom: '20px', left: '20px', opacity: isDark ? .15 : .06, display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '10px', pointerEvents: 'none', zIndex: 0 }}>
                        {[...Array(25)].map((_, i) => <div key={i} style={{ width: '4px', height: '4px', borderRadius: '50%', background: isDark ? '#C1348B' : '#0f172a' }} />)}
                    </div>
                    <div style={{ maxWidth: '820px', margin: '0 auto', padding: 'clamp(80px,12vw,120px) 32px clamp(40px,6vw,60px)', position: 'relative', zIndex: 1 }}>
                        <div className="det-in" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
                            <button className="det-btn-back" onClick={() => navigate('/blog')}>← Blog</button>
                            {post.categoria && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: `${color}15`, border: `1px solid ${color}35`, color: isDark ? '#FFD97C' : color, borderRadius: '100px', padding: '4px 12px', fontSize: '.72rem', fontWeight: '700' }}>
                                    {post.categoria.nombre}
                                </span>
                            )}
                            {post.es_destacado && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: `${colors.accent}20`, border: `1px solid ${colors.accent}35`, color: colors.accentDark, borderRadius: '100px', padding: '4px 12px', fontSize: '.72rem', fontWeight: '700' }}>
                                    ⭐ Destacado
                                </span>
                            )}
                        </div>
                        <h1 className="det-in" style={{ fontSize: 'clamp(1.8rem,4vw,3rem)', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.1', letterSpacing: '-.025em', marginBottom: '16px' }}>
                            {post.titulo}
                        </h1>
                        {post.subtitulo && (
                            <p className="det-in-1" style={{ fontSize: 'clamp(1rem,2vw,1.25rem)', color: isDark ? '#cbd5e1' : '#64748b', lineHeight: '1.7', marginBottom: '28px', fontWeight: '500' }}>
                                {post.subtitulo}
                            </p>
                        )}
                        <div className="det-in-1" style={{ display: 'flex', gap: '5px', marginBottom: '28px' }}>
                            {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                                <div key={i} style={{ height: '3px', width: i === 0 ? '28px' : '9px', borderRadius: '3px', background: c }} />
                            ))}
                        </div>
                        <div className="det-in-2" style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: `linear-gradient(135deg,${color}cc,${color}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.85rem', fontWeight: '900', color: '#fff', flexShrink: 0 }}>
                                {initials(post.nombre_autor || 'Au')}
                            </div>
                            <div>
                                <div style={{ fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', fontSize: '.92rem' }}>{post.nombre_autor}</div>
                                <div style={{ fontSize: '.75rem', color: isDark ? '#94a3b8' : '#94a3b8', marginTop: '2px' }}>{fmtDate(post.fecha_publicacion)}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* IMAGEN COVER */}
                <div style={{ maxWidth: '900px', margin: '-20px auto 0', padding: '0 32px' }}>
                    <div style={{
                        borderRadius: '24px', overflow: 'hidden',
                        boxShadow: isDark ? '0 24px 60px rgba(0,0,0,.6)' : '0 24px 60px rgba(0,0,0,.1)',
                        border: isDark ? '1.5px solid rgba(193,52,139,0.3)' : '1.5px solid #f1f5f9',
                        position: 'relative', height: '360px', background: grad
                    }}>
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 0 }}>
                            <span style={{ fontSize: '6rem', filter: 'drop-shadow(0 8px 24px rgba(0,0,0,.2))' }}>{emoji}</span>
                        </div>
                        {imgSrc && !imgError && (
                            <img src={imgSrc} alt={post.titulo}
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1, display: 'block' }}
                                onError={() => setImgError(true)} />
                        )}
                    </div>
                </div>

                {/* CONTENIDO */}
                <div style={{ maxWidth: '820px', margin: '0 auto', padding: '48px 32px 80px' }}>
                    <div
                        className="det-prose"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.contenido) }}
                    />
                    <div style={{
                        marginTop: '48px', paddingTop: '28px',
                        borderTop: isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: `linear-gradient(135deg,${color}cc,${color}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.85rem', fontWeight: '900', color: '#fff' }}>
                                {initials(post.nombre_autor || 'Au')}
                            </div>
                            <div>
                                <div style={{ fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', fontSize: '.9rem' }}>{post.nombre_autor}</div>
                                <div style={{ fontSize: '.72rem', color: isDark ? '#94a3b8' : '#94a3b8' }}>Publicado el {fmtDate(post.fecha_publicacion)}</div>
                            </div>
                        </div>
                        <button className="det-btn-back" onClick={() => navigate('/blog')}>← Volver al blog</button>
                    </div>
                </div>

                {/* MÁS ARTÍCULOS */}
                {otros.length > 0 && (
                    <div style={{
                        background: isDark ? '#0e071a' : '#fff',
                        borderTop: isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9',
                        padding: '56px 32px 72px',
                        transition: 'background 0.3s ease',
                    }}>
                        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                <span style={{ fontSize: '.72rem', fontWeight: '700', letterSpacing: '.14em', textTransform: 'uppercase', color: colors.secondary }}>Sigue leyendo</span>
                                <div style={{ height: '2px', width: '40px', borderRadius: '2px', background: colors.secondary }} />
                            </div>
                            <h2 style={{ fontSize: 'clamp(1.3rem,2.5vw,1.8rem)', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-.02em', marginBottom: '28px' }}>Más artículos del blog</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '18px' }}>
                                {otros.map((p, i) => {
                                    const c = postColor(i);
                                    const g = postGrad(i);
                                    const em = postEmoji(i);
                                    const pImg = p.url_imagen || p.imagen || null;
                                    return (
                                        <BorderGlow
                                            key={p.id}
                                            borderRadius={20}
                                            backgroundColor={isDark ? '#150d24' : '#ffffff'}
                                            onClick={() => navigate(`/blog/${p.slug}`)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div style={{ height: '160px', position: 'relative', overflow: 'hidden', background: g }}>
                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: g, zIndex: 0 }}>
                                                    <span style={{ fontSize: '2.8rem' }}>{em}</span>
                                                </div>
                                                {pImg && (
                                                    <img src={pImg} alt={p.titulo}
                                                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 1 }}
                                                        onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                                                )}
                                                <div style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(0,0,0,.3)', backdropFilter: 'blur(6px)', borderRadius: '6px', padding: '2px 9px', fontSize: '.65rem', fontWeight: '700', color: '#fff', zIndex: 2 }}>
                                                    {p.categoria?.nombre || 'General'}
                                                </div>
                                            </div>
                                            <div style={{ padding: '18px 18px 20px' }}>
                                                <h3 style={{ fontSize: '.95rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.35', marginBottom: '8px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {p.titulo}
                                                </h3>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span style={{ fontSize: '.72rem', color: isDark ? '#94a3b8' : '#94a3b8' }}>{fmtDate(p.fecha_publicacion)}</span>
                                                    <span style={{ fontSize: '.78rem', fontWeight: '700', color: isDark ? '#FFD97C' : c }}>Leer →</span>
                                                </div>
                                            </div>
                                        </BorderGlow>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default BlogDetalle;